/**
 * @name ChangePasswordView.js
 * @description 비밀번호 강제 변경 - 로그인 모달 내부 전용 뷰
 * - 임시 비밀번호로 로그인한 사용자가 모달 안에서 바로 변경
 * - 변경 완료 시 onSuccess() 콜백 호출 (모달 닫기 + /main 이동)
 */
import React, { useState, useContext } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { post } from '@utils/axios_api';
import { AuthContext } from '@context';

const ChangePasswordView = ({ onSuccess, onClose }) => {
  const { auth, logout, clearForceChangePw } = useContext(AuthContext);
  const [form, setForm] = useState({ password: '', confirmPassword: '' });
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.password || !form.confirmPassword) {
      setErrorMsg('새 비밀번호를 모두 입력해 주세요.');
      return;
    }
if (form.password !== form.confirmPassword) {
      setErrorMsg('비밀번호가 일치하지 않습니다.');
      return;
    }

    setIsLoading(true);
    try {
      await post('/auth/change-password', { password: form.password }, {
        headers: { 'Content-Type': 'application/json' },
      });
      clearForceChangePw();
      setDone(true);
    } catch (err) {
      console.error('비밀번호 변경 오류:', err);
      setErrorMsg('서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    onClose();
    logout();
  };

  /* ── 변경 완료 화면 ── */
  if (done) {
    return (
      <div className="login-form" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div className="logo text-center mb-3">
          <img src="/img/logo.png" alt="회사 로고" style={{ width: '100px', height: 'auto' }} />
        </div>

        <div style={{
          background: '#e8f4fd',
          border: '1px solid #2196F3',
          borderRadius: '6px',
          padding: '24px 16px',
          marginBottom: '16px',
          textAlign: 'center',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
        }}>
          <CheckCircle2 size={36} color="#1976D2" />
          <p style={{ fontWeight: 'bold', color: '#1976D2', fontSize: '1rem', margin: 0 }}>
            비밀번호가 변경되었습니다.
          </p>
          <p style={{ color: '#555', fontSize: '0.85rem', margin: 0 }}>
            새 비밀번호로 서비스를 이용해 주세요.
          </p>
        </div>

        <button
          type="button"
          className="login-button"
          onClick={onSuccess}
          style={{ marginTop: '8px' }}
        >
          시작하기
        </button>
      </div>
    );
  }

  /* ── 입력 화면 ── */
  return (
    <div className="login-form" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div className="logo text-center mb-3">
        <img src="/img/logo.png" alt="회사 로고" style={{ width: '100px', height: 'auto' }} />
      </div>

      <h1 className="text-center fs-4 fw-bold mb-2">비밀번호 변경</h1>

      <div style={{
        background: '#fff3e0',
        border: '1px solid #FF9800',
        borderRadius: '6px',
        padding: '10px 14px',
        marginBottom: '16px',
        fontSize: '0.85rem',
        color: '#e65100',
        textAlign: 'center',
      }}>
        ⚠️ 임시 비밀번호로 로그인하셨습니다.<br />
        보안을 위해 새 비밀번호로 변경해 주세요.
      </div>

      <p style={{ color: '#666', marginBottom: '12px', fontSize: '0.9rem', textAlign: 'center' }}>
        <strong>{auth?.userId}</strong> 계정의 비밀번호를 변경합니다.
      </p>

      {errorMsg && (
        <p style={{ color: 'red', fontSize: '0.85rem', marginBottom: '10px' }}>{errorMsg}</p>
      )}

      <form onSubmit={handleSubmit}>
        <div className="login_input_wrap">
          <input
            type="password"
            name="password"
            placeholder="새 비밀번호를 입력해 주세요."
            value={form.password}
            onChange={handleChange}
            maxLength="20"
          />
          <input
            type="password"
            name="confirmPassword"
            placeholder="새 비밀번호를 다시 입력해 주세요."
            value={form.confirmPassword}
            onChange={handleChange}
            maxLength="20"
          />
        </div>
        <button
          type="submit"
          className="login-button"
          disabled={isLoading}
          style={{ marginTop: '16px' }}
        >
          {isLoading ? '변경 중...' : '비밀번호 변경'}
        </button>
      </form>

      {/* 다른 뷰 하단 링크와 동일한 스타일 */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'auto', paddingTop: '12px', fontSize: '0.9rem' }}>
        <button
          type="button"
          onClick={handleLogout}
          style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer' }}
        >
          로그아웃
        </button>
      </div>
    </div>
  );
};

export default ChangePasswordView;
